// ランダム（2026-10-21 に app.js から分けた）
import { S } from './state.js';
import { catLabel, catShort, txt, txtHtml } from './text.js';
import { PAIRS, layerSlot, mergedOf, slotKeys } from './merge.js';
import { catByKey } from './store.js';
import { $, keyOf } from './util.js';
import { mix } from './colormath.js';
import { canPattern } from './core/pattern.js';
import { partsList } from './core/data.js';
import { C, blankLine } from './core/model.js';
import { categoryIcon, selectCategory } from './categories.js';
import { hexOf } from './colors.js';
import { groupOf, isLinked, linkFamily } from './colorlink.js';
import { pop } from './parts.js';
import { histCut, snapshot, toast } from './history.js';
import { closeModal, openModalNode } from './dialog.js';
import type { Category, PartState } from './types.js';
import { replaceState } from './core/ops.js';
import { EXCLUSIVE, clearExclusive } from './core/exclusive.js';

// ---- ランダム（パーツの選び方は chibi.loadRandom → makeCharactor と同じ） ----
// 設定（モックだけのもの。本物の CHOCO は「元の色に戻す」と同じで、毎回パーツの元の色に戻る）:
//   color: 'reset' 元の色に戻す / 'keep' 色はそのまま（選んだ色・グラデーションを残す）/ 'random' 色もランダム（肌の色はそのまま）
//   locks: 変えないカテゴリ（鍵をかけたものは、パーツも色も位置もそのまま）
// 保存はモックだけの場所（本物の chibi_* には書かない）
export const RND_KEY = 'charatCreate.random';
export const COLOR_FIELDS: ('col' | 'from' | 'to' | 'lfrom' | 'lto' | 'rfrom' | 'rto')[] = ['col', 'from', 'to', 'lfrom', 'lto', 'rfrom', 'rto'];
export const COLOR_MODES = [
  { id: 'reset', label: 'rnd_reset', note: 'rnd_reset_note' },
  { id: 'keep', label: 'rnd_keep', note: 'rnd_keep_note' },
  { id: 'random', label: 'rnd_random', note: 'rnd_random_note' }
];
export const rndOpt = loadRndOpt();
export function loadRndOpt(): { color: string; locks: Record<string, boolean> } {
  try {
    const o = JSON.parse(localStorage.getItem(RND_KEY) || 'null');
    if (o && COLOR_MODES.some((m) => m.id === o.color)) return { color: o.color, locks: o.locks || {} };
  } catch (e) {}
  return { color: 'keep', locks: {} };
}
export function saveRndOpt() { try { localStorage.setItem(RND_KEY, JSON.stringify(rndOpt)); } catch (e) {} }
export function isLocked(key: string) { return !!rndOpt.locks[key] && !!catByKey[key]; }
export function lockCount() { return Object.keys(rndOpt.locks).filter(isLocked).length; }
export function setLock(key: string, on: boolean) {
  if (on) rndOpt.locks[key] = true; else delete rndOpt.locks[key];
  saveRndOpt();
  refreshLocks();
}
// 鍵の印（カテゴリの列・PC の見出しの「固定」・▾ の数）
export function refreshLocks() {
  // 重ね付けのカテゴリは、どちらかの置き場所に鍵があれば印
  document.querySelectorAll<HTMLElement>('.cat').forEach((b) => { const p = slotKeys(b.dataset.key!); b.classList.toggle('locked', isLocked(b.dataset.key!) || !!(p && isLocked(p[1]))); });
  const btn = $('btnLock'), cat = catByKey[S.selKey];
  btn.hidden = !cat || cat.NAME === 'chara';
  btn.classList.toggle('on', isLocked(S.selKey));
  btn.setAttribute('aria-pressed', isLocked(S.selKey) ? 'true' : 'false');
  btn.querySelector('span')!.textContent = txt(isLocked(S.selKey) ? 'locked' : 'lock');
  const n = lockCount();
  $('rndBadge').hidden = !n;
  $('rndBadge').textContent = String(n);
}
export function hasColor(st: PartState | undefined) { return !!st && COLOR_FIELDS.some((f) => st[f]); }
export function copyColors(st: PartState, from: PartState | undefined) { if (from) COLOR_FIELDS.forEach((f) => { st[f] = from[f]; }); }
// 残す色: そのカテゴリに色があればそれ、無ければ同じグループ（肌・髪）のカテゴリの色（新しくパーツが付いたときも揃うように）
export function colorSource(cat: Category) {
  const own = C.state[keyOf(cat)];
  if (hasColor(own) || !isLinked(cat)) return own;
  return groupOf(cat).map((c) => C.state[keyOf(c)]).filter(hasColor)[0] || own;
}
export function randomPalette() { return String(C.data.PALLET[Math.floor(Math.random() * C.data.PALLET.length)][0]).toLowerCase(); }
// 色もランダム: グループごとに1色（肌はそのまま、グループに固定したカテゴリがあればその色に揃える）。
// グラデーションは上をその色、下をその色を白に寄せた色に
export function randomColors(st: PartState, cat: Category, picks: Record<string, string>) {
  if (linkFamily(cat) === 'skin') { copyColors(st, colorSource(cat)); return; }
  const mate = isLinked(cat) ? groupOf(cat).filter((c) => isLocked(keyOf(c)) && hasColor(C.state[keyOf(c)]))[0] : null;
  if (mate) { copyColors(st, C.state[keyOf(mate)]); return; }
  const gkey = isLinked(cat) ? linkFamily(cat)! : keyOf(cat);
  const pick = picks[gkey] || (picks[gkey] = randomPalette());
  if (cat.GRAD.length > 1) { st.from = pick; st.to = hexOf(mix(pick, '#ffffff', 0.55)); }
  else if (cat.COLOR.length) st.col = pick;
}
export function randomize() {
  histCut();
  const snap = snapshot();
  const next: Record<string, PartState> = {}, picks: Record<string, string> = {};
  C.cats.forEach((cat) => {
    const key = keyOf(cat);
    if (cat.NAME === 'chara') return;
    if (isLocked(key)) { if (C.state[key]) next[key] = C.state[key]; return; }
    if (!(cat.RND >= 1 || cat.LAYER[0] === 'bg')) { if (C.state[key]) next[key] = C.state[key]; return; }
    const list = partsList(cat.NAME);
    if (!list.length) return;
    const part = cat.RND <= 1 ? list[Math.floor(Math.random() * list.length)] : list[cat.RND - 2];
    if (!part) return;
    const st = blankLine();
    st.parts = part.id;
    if (cat.REVERSE == 1) st.rev = 1; // 持ちもの（右）は反対の手に
    if (rndOpt.color === 'keep') {
      copyColors(st, colorSource(cat));
      if (canPattern(cat) && C.state[key]) { st.ptn = C.state[key].ptn; st.prev = C.state[key].prev; st.ptnmode = C.state[key].ptnmode; } // 柄も残す
      if (C.state[key]) st.opacity = C.state[key].opacity; // うすさも
    }
    else if (rndOpt.color === 'random') randomColors(st, cat, picks);
    next[key] = st;
  });
  Object.keys(PAIRS).forEach((a) => {
    const b = PAIRS[a];
    if (mergedOf[b] !== a || layerSlot[a]) return;
    if (isLocked(a) || isLocked(b)) { [a, b].forEach((k) => { if (C.state[k]) next[k] = C.state[k]; else delete next[k]; }); return; }
    if (next[a]) next[b] = JSON.parse(JSON.stringify(next[a])); else delete next[b];
  });
  // 付けると外れるカテゴリ（KAWAII の手）: カテゴリの順に、付いているものの相手を外す（変えないカテゴリの相手は、こちらを外す）
  C.cats.forEach((cat) => {
    const key = keyOf(cat);
    if (!EXCLUSIVE[key] || !next[key]) return;
    if (EXCLUSIVE[key].some((k) => isLocked(k) && next[k] && Number(next[k].parts) !== Number((partsList(catByKey[k].NAME)[0] || { id: -1 }).id))) {
      if (!isLocked(key)) next[key].parts = (partsList(cat.NAME)[0] || { id: next[key].parts }).id;
      return;
    }
    clearExclusive(key, next, false);
  });
  replaceState(next);
  if (S.selKey) selectCategory(S.selKey);
  pop();
  const n = lockCount();
  toast(n ? txt('rnd_done_locked', { n: n }) : txt('rnd_done'), snap);
}

// ランダムの設定（色の扱い・変えないカテゴリ）
export function openRandomSettings() {
  var root = document.createElement('div');
  root.className = 'rnd';
  root.innerHTML =
    '<h2>' + txtHtml('random_opt') + '</h2>' +
    '<h3>' + txtHtml('rnd_color') + '</h3><div class="seg" role="radiogroup" aria-label="' + txtHtml('rnd_color') + '"></div><p class="note seg-note"></p>' +
    '<h3>' + txtHtml('rnd_locks') + ' <span class="lock-n"></span><button type="button" class="text-btn lock-clear">' + txtHtml('rnd_clear') + '</button></h3>' +
    '<p class="note">' + txtHtml('rnd_locks_note') + '</p>' +
    '<div class="lock-grid"></div>';
  var seg = root.querySelector('.seg')!, grid = root.querySelector('.lock-grid')!;
  COLOR_MODES.forEach((m) => {
    const label = document.createElement('label');
    label.innerHTML = '<input type="radio" name="rndColor" value="' + m.id + '"><span>' + txtHtml(m.label) + '</span>';
    const input = label.querySelector('input')!;
    input.checked = rndOpt.color === m.id;
    input.addEventListener('change', () => { rndOpt.color = m.id; saveRndOpt(); draw(); });
    seg.appendChild(label);
  });
  C.cats.forEach((cat) => {
    const key = keyOf(cat);
    if (cat.NAME === 'chara') return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'lock-cat' + (key === S.selKey ? ' now' : '');
    b.dataset.key = key;
    b.title = catLabel(key);
    b.appendChild(categoryIcon(cat));
    const t = document.createElement('span');
    t.textContent = catShort(key);
    b.appendChild(t);
    b.addEventListener('click', () => { setLock(key, !isLocked(key)); draw(); });
    grid.appendChild(b);
  });
  root.querySelector('.lock-clear')!.addEventListener('click', () => { rndOpt.locks = {}; saveRndOpt(); refreshLocks(); draw(); });
  const go = document.createElement('button');
  go.type = 'button';
  go.className = 'cta';
  go.innerHTML = $('btnRandom').querySelector('svg')!.outerHTML + '<span>' + txtHtml('rnd_go') + '</span>';
  go.addEventListener('click', () => { closeModal(); randomize(); });
  function draw() {
    root.querySelector('.seg-note')!.textContent = txt(COLOR_MODES.filter((m) => m.id === rndOpt.color)[0].note);
    grid.querySelectorAll<HTMLElement>('.lock-cat').forEach((b) => {
      const on = isLocked(b.dataset.key!);
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const n = lockCount();
    root.querySelector('.lock-n')!.textContent = n ? txt('rnd_locks_n', { n: n }) : '';
    root.querySelector<HTMLElement>('.lock-clear')!.hidden = !n;
  }
  draw();
  openModalNode(root, [go]);
  // 入りきらない名前（ほかの言語の長いもの）は少し小さい字に
  grid.querySelectorAll('.lock-cat span').forEach((t) => { if (t.scrollWidth > t.clientWidth) t.classList.add('tight'); });
}
